import {
  Lock,
  Login as LoginIcon,
  Person,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { logError } from '@ptek/client-logger';
import { useBrandTheme } from '@ptek/widget-core';
import React, { useState } from 'react';
import { FlexColumn, IconCircle } from '../../../components/LayoutBoxes';
import { useWidgetAuth } from '../../../context/AuthContext';
import { iconStyles } from '../../../styles/iconStyles';
import { authStyles as styles } from '../styles/authStyles';

/**
 * Compact login form designed specifically for widget mode
 * Clean, modern design optimized for small spaces
 */
export const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error } = useWidgetAuth();
  const brand = useBrandTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) return;

    try {
      await login({ username, password });
    } catch (err) {
      logError('LoginForm', 'Login failed:', err);
    }
  };

  return (
    <FlexColumn sx={styles.loginCard}>
      {/* Header with icon */}
      <Box sx={styles.loginHeader}>
        <IconCircle sx={styles.loginIconCircle}>
          <LoginIcon sx={iconStyles.circleIcon} />
        </IconCircle>
        <Typography variant='dialogTitle' component='h2' sx={styles.loginTitle}>
          Welcome Back
        </Typography>
        <Typography variant='dialogBody' component='p' sx={styles.loginSubtitle}>
          Sign in to {brand.brandName}
        </Typography>
      </Box>

      {/* Form Section */}
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={styles.loginForm}
      >
        <TextField
          label='Email'
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={isLoading}
          variant='outlined'
          size='medium'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Person sx={styles.inputIcon} />
              </InputAdornment>
            ),
          }}
          sx={styles.textFieldOverride}
        />

        <TextField
          label='Password'
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
          variant='outlined'
          size='medium'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Lock sx={styles.inputIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                  disabled={isLoading}
                  size='small'
                  sx={styles.visibilityToggle}
                >
                  {showPassword ? (
                    <VisibilityOff sx={iconStyles.lg} />
                  ) : (
                    <Visibility sx={iconStyles.lg} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={styles.textFieldOverride}
        />

        <Button
          variant='contained'
          type='submit'
          disabled={isLoading || !username || !password}
          size='large'
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <LoginIcon sx={iconStyles.lg} />
            )
          }
          sx={styles.loginSubmitButton}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Error display */}
        {error && (
          <Alert severity='error' variant='outlined' sx={styles.loginErrorAlert}>
            {error.message || 'Login failed. Please try again.'}
          </Alert>
        )}
      </Box>
    </FlexColumn>
  );
};
